import type {
  ChatItem,
  ToolUseItem,
  ToolUseStatus,
} from '../../../../contracts/harness.types';
import { TOOL_PRESENTATION_REGISTRY } from '../presentation/tool-registry';
import type { ToolUseGroupDecision } from '../presentation/types';
import {
  summariseToolGroup,
  type ToolGroupSummary,
  type ToolGroupTone,
} from './toolGroupSummary';

/** `preparing`: the model is still writing the call, so it has no label to name yet. */
export type RowPhase = 'preparing' | 'running' | 'succeeded' | 'failed' | 'cancelled';

/**
 * One row the clinician would see on expanding a Working block — the unit the
 * block's line names and counts. A same-name group renders as one row.
 */
export interface WorkRow {
  /** The call, or a group's head. */
  item: ToolUseItem;
  /** Visible calls behind this row: 1, or the group's size. */
  count: number;
  /** Of those, how many succeeded — the grouped row's partial-success wording needs it. */
  done: number;
  phase: RowPhase;
  /** The row's label names the work (`isNamed`); the line never shows one that does not. */
  named: boolean;
  /** A completed-tense label exists (`hasPastTenseLabel`). */
  hasPastTense: boolean;
  /** When the row was active: the call's frames, or the whole group's. */
  seenAtMs?: { first: number; last: number };
}

/** `queued` and `pending_approval` are not rows: no execution seen, or an interaction. */
const phaseOf = (status: ToolUseStatus): RowPhase | null => {
  switch (status) {
    case 'running':
      return 'running';
    case 'streaming_input':
      return 'preparing';
    case 'complete':
      return 'succeeded';
    case 'error':
      return 'failed';
    case 'cancelled':
      return 'cancelled';
    case 'queued':
    case 'pending_approval':
      return null;
  }
};

const phaseOfTone: Record<ToolGroupTone, RowPhase> = {
  running: 'running',
  success: 'succeeded',
  error: 'failed',
  cancelled: 'cancelled',
};

/**
 * A group has started once any member has; until then it is not a row, as a
 * queued call is not. A group whose started members are all still being written
 * is preparing: nothing has run that the line could name.
 */
const groupPhaseOf = (summary: ToolGroupSummary): RowPhase | null => {
  const phases = summary.members.map((member) => phaseOf(member.status));
  if (phases.every((phase) => phase === null)) return null;
  if (phases.every((phase) => phase === null || phase === 'preparing'))
    return 'preparing';
  return phaseOfTone[summary.tone];
};

const hasRegistryLabel = (name: string): boolean =>
  TOOL_PRESENTATION_REGISTRY[name]?.label !== undefined;

/**
 * Whether the row's label names the work. A counted group of a tool the registry
 * does not know reads "Running 3 tools" / "Ran 3 tools": a tool count, not a name.
 * A group filtered down to one visible call is labelled as that call.
 */
const isNamed = (name: string, count: number): boolean =>
  count < 2 || hasRegistryLabel(name);

/** A completed-tense label exists: a registry label, or failure wording over a name. */
const hasPastTenseLabel = (name: string, phase: RowPhase, count: number): boolean =>
  isNamed(name, count) &&
  (phase === 'failed' || phase === 'cancelled' || hasRegistryLabel(name));

/** The grouped row names one tool, so only a same-name run folds into it. */
const isSameNameRun = (calls: readonly ToolUseItem[]): boolean =>
  calls.every((call) => call.name === calls[0]?.name);

/** `isVisible` is the timeline policy's row visibility, so a hidden failure is never a row. */
export const deriveWorkRows = (
  members: readonly ChatItem[],
  isVisible: (item: ToolUseItem) => boolean,
  groups: ReadonlyMap<string, ToolUseGroupDecision>
): WorkRow[] => {
  const rows: WorkRow[] = [];
  // A mixed run renders each call as its own row, so each is its own row here.
  const unfolded = new Set<string>();
  for (const member of members) {
    if (member.kind !== 'tool_use') continue;
    const group = groups.get(member.id);
    if (group?.role === 'start' && !isSameNameRun(group.members)) {
      for (const call of group.members) unfolded.add(call.id);
    }

    if (group && !unfolded.has(member.id)) {
      if (group.role === 'member') continue;
      const summary = summariseToolGroup(group.members);
      const [head] = summary?.members ?? [];
      const phase = summary && groupPhaseOf(summary);
      if (!summary || !head || !phase) continue;
      rows.push({
        item: head,
        count: summary.total,
        done: summary.done,
        phase,
        named: isNamed(head.name, summary.total),
        hasPastTense: hasPastTenseLabel(head.name, phase, summary.total),
        ...(summary.seenAtMs ? { seenAtMs: summary.seenAtMs } : {}),
      });
      continue;
    }

    if (!isVisible(member)) continue;
    const phase = phaseOf(member.status);
    if (phase === null) continue;
    rows.push({
      item: member,
      count: 1,
      done: phase === 'succeeded' ? 1 : 0,
      phase,
      named: true,
      hasPastTense: hasPastTenseLabel(member.name, phase, 1),
      ...(member.seenAtMs ? { seenAtMs: member.seenAtMs } : {}),
    });
  }
  return rows;
};
